import { getLocale, type Locale } from "../intl";

import type { LegalPage } from "./legal-pages.types";

/**
 * As páginas legais moram no web. O app e o desktop abrem essas mesmas rotas
 * no navegador, na URL do web do ambiente — não o site institucional (decisão
 * do dono, 2026-09-29).
 */
export const LEGAL_PAGE_SLUGS = {
  termsOfUse: "termos-de-uso",
  privacyPolicy: "politica-de-privacidade",
} as const satisfies Record<LegalPage, string>;

/**
 * Prefixo de idioma das páginas legais no web. O português fica sem prefixo
 * (`/termos-de-uso`, o endereço de sempre); inglês e espanhol ganham `/en` e
 * `/es`. Tem de casar com `LOCALE_SEGMENTS` do web
 * (`apps/web/src/lib/i18n/locale-path.ts`) — um teste do web confere.
 */
const LEGAL_PAGE_LOCALE_PREFIX = {
  "pt-BR": "",
  "en-US": "/en",
  "es-ES": "/es",
} as const satisfies Record<Locale, string>;

/** Rota da página legal no idioma pedido (padrão: o idioma de agora). */
export function legalPagePath(page: LegalPage, locale: Locale = getLocale()): string {
  return `${LEGAL_PAGE_LOCALE_PREFIX[locale]}/${LEGAL_PAGE_SLUGS[page]}`;
}

/**
 * Rotas das páginas legais no idioma de agora. Getters: lidos no render, para
 * a troca de idioma valer sem recarregar o módulo.
 */
export const LEGAL_PAGE_PATHS: { readonly termsOfUse: string; readonly privacyPolicy: string } = {
  get termsOfUse() {
    return legalPagePath("termsOfUse");
  },
  get privacyPolicy() {
    return legalPagePath("privacyPolicy");
  },
};

/**
 * Segmento de idioma da página embutível. Diferente das páginas públicas, o
 * português leva o segmento: a rota embutível não passa pelo proxy de idioma.
 */
const LEGAL_EMBED_LOCALE_SEGMENT = {
  "pt-BR": "pt",
  "en-US": "en",
  "es-ES": "es",
} as const satisfies Record<Locale, string>;

/** Os nomes do tema na query da rota embutível. */
const LEGAL_EMBED_THEME = { light: "claro", dark: "escuro" } as const;

/**
 * Rota do web com só o corpo dos Termos de Uso, sem cabeçalho nem banner de
 * cookies: é o que o reaceite embute (iframe no web e no desktop, WebView no
 * app — TAM-305). O tema vai na query porque a página é estática.
 */
export function termsOfUseEmbedPath(theme: "light" | "dark", locale: Locale = getLocale()): string {
  return `/embed/${LEGAL_EMBED_LOCALE_SEGMENT[locale]}/${LEGAL_PAGE_SLUGS.termsOfUse}?tema=${LEGAL_EMBED_THEME[theme]}`;
}

/** URL absoluta de uma rota do web, a partir da base do ambiente. */
export function webPageUrl(webBaseUrl: string, path: string): string {
  return `${webBaseUrl.replace(/\/+$/u, "")}${path}`;
}
